import { type BACKGROUND_COLORS_VALUES, COLORS } from '@alto/design-library-tokens';
import { getBrand } from './getBrand';

/**
 * Header colours that carry meaning rather than branding. A warning or danger header is the signal
 * that the patient has to do something — confirm a shipment, update a payment method, add insurance
 * (see `fillHomescreenBanner`) — so the FuzeRx neutral override below must not flatten them. Read
 * from `COLORS` at call time, not at module load, because the store is swapped when the brand
 * resolves.
 */
const getWarningHeaderBackgroundColors = (): BACKGROUND_COLORS_VALUES[] => [
  COLORS.BACKGROUND_COLORS.WARNING,
  COLORS.BACKGROUND_COLORS.WARNING_LIGHT,
  COLORS.BACKGROUND_COLORS.WARNING_LIGHTER,
  COLORS.BACKGROUND_COLORS.WARNING_LIGHTEST,
];

const getDangerHeaderBackgroundColors = (): BACKGROUND_COLORS_VALUES[] => [
  COLORS.BACKGROUND_COLORS.DANGER,
  COLORS.BACKGROUND_COLORS.DANGER_LIGHT,
  COLORS.BACKGROUND_COLORS.DANGER_LIGHTER,
  COLORS.BACKGROUND_COLORS.DANGER_LIGHTEST,
];

/**
 * FuzeRx puts every page header on the same neutral surface (Surface/neutral/base, PH-4205), so the
 * per-page colour the other brands pass is ignored for that brand:
 *
 * - a warning header (pending confirmation, payment or insurance task) becomes container.warning.subtle
 *   — `WARNING_LIGHTEST`, `#fff2b9` — whichever warning shade the page asked for;
 * - a danger header keeps the shade it asked for;
 * - everything else becomes `GREY_LIGHTEST`, `#f7f4f2`.
 *
 * The values come from the brand's own tokens rather than hardcoded hexes, so they stay in step with
 * the rest of the brand's surfaces.
 *
 * Most headers render through `PageHeaderSection`, but the pages also hand the same colour to the
 * `NavBar` and the nav-bar offset spacer, and the homescreen builds its own header on each platform
 * (`AnimatedPageWithHeader` on native, `PageWithHeader` on web). Every one of those resolves through
 * this helper so the band above the title and the title itself can't end up in two colours: the same
 * decision duplicated across components is what let the filter tag drift out of sync in PH-4204.
 */
export const resolvePageHeaderBackgroundColor = <T extends BACKGROUND_COLORS_VALUES | null | undefined>(
  backgroundColor: T,
): T | BACKGROUND_COLORS_VALUES => {
  if (getBrand() !== 'fuzerx') {
    return backgroundColor;
  }

  if (backgroundColor && getWarningHeaderBackgroundColors().includes(backgroundColor)) {
    return COLORS.BACKGROUND_COLORS.WARNING_LIGHTEST;
  }

  if (backgroundColor && getDangerHeaderBackgroundColors().includes(backgroundColor)) {
    return backgroundColor;
  }

  return COLORS.BACKGROUND_COLORS.GREY_LIGHTEST;
};
